'use client'

import Image from 'next/image'
import { useEffect, useState } from 'react'

import { stegaClean } from 'next-sanity'

import { Button } from '@/app/components/Button'
import { LinkProps } from '@/app/components/Link'
import { TrackingLocation } from '@/app/utils/analytics'
import { cn } from '@/app/utils/style'
import { fetchSvgContent } from '@/app/utils/svg'
import {
  AdvertiserCallout as AdvertiserCalloutRaw,
  Icon,
  SanityImageCrop,
  SanityImageHotspot,
} from '@/sanity.types'
import { dataAttr, urlForImage } from '@/sanity/lib/utils'
import { DeepRequiredNullable } from '@/types/utils'

export type AdvertiserCalloutWithDeref = Omit<
  AdvertiserCalloutRaw,
  'advertiser' | 'publisherCompany' | 'button' | 'monochromeLogo'
> & {
  monochromeLogo?: boolean | null
  advertiser?: {
    _id: string
    companyName: string
    logo: {
      asset: {
        _ref: string
        _type: 'reference'
      } | null
      alt: null
      crop: SanityImageCrop | null
      hotspot: SanityImageHotspot | null
    } | null
  } | null
  publisherCompany?: {
    _id: string
    companyName: string
    calloutLogo: {
      asset: {
        _ref: string
        _type: 'reference'
      } | null
      alt: null
      crop: SanityImageCrop | null
      hotspot: SanityImageHotspot | null
    } | null
    logo: {
      asset: {
        _ref: string
        _type: 'reference'
      } | null
      alt: null
      crop: SanityImageCrop | null
      hotspot: SanityImageHotspot | null
    } | null
  } | null
  button?: {
    label: string
    link: LinkProps['link']
    icon?: DeepRequiredNullable<Icon> | null
    dataId?: string | null
  } | null
}

export type AdvertiserCalloutProps = {
  advertiserCallout: AdvertiserCalloutWithDeref & { _key?: string }
  tracking: TrackingLocation
  className?: string
  documentId?: string
  documentType?: string
  parentPath?: string
}

const themeClasses = {
  neon: {
    container: 'bg-neon-200',
    text: '!text-white-950',
    button: { variant: 'secondary' as const, size: 'sm' as const },
  },
  purple: {
    container: 'bg-purple-700',
    text: '!text-white-default',
    button: { variant: 'primary' as const, size: 'sm' as const },
  },
  white: {
    container: 'bg-white-default',
    text: '!text-white-950',
    button: { variant: 'primary' as const, size: 'sm' as const },
  },
}

export const AdvertiserCallout = ({
  advertiserCallout,
  tracking,
  className,
  documentId,
  documentType,
  parentPath,
}: AdvertiserCalloutProps) => {
  const theme = stegaClean(advertiserCallout?.theme) || 'purple'
  const themeConfig = themeClasses[theme as keyof typeof themeClasses]
  const hasTitle = !!advertiserCallout?.title
  const monochrome = stegaClean(advertiserCallout?.monochromeLogo) ?? false

  const sourceType = stegaClean(advertiserCallout?.sourceType) || 'advertiser'
  const company =
    sourceType === 'publisherCompany'
      ? advertiserCallout?.publisherCompany
      : advertiserCallout?.advertiser
  const [logoSvg, setLogoSvg] = useState<string | null>(null)

  // Determine monochrome filter based on theme
  const getMonochromeFilter = () => {
    if (!monochrome) return ''
    // Purple background → white logo
    if (theme === 'purple') return 'brightness-0 invert'
    // Neon/white backgrounds → black logo
    return 'brightness-0'
  }

  const publisherCompany = advertiserCallout?.publisherCompany
  const logoToUse = publisherCompany?.calloutLogo || publisherCompany?.logo
  const logoUrl =
    sourceType === 'publisherCompany' && logoToUse ? urlForImage(logoToUse)?.url() : null

  useEffect(() => {
    const fetchLogo = async () => {
      if (sourceType === 'advertiser' && company?.logo) {
        const svg = await fetchSvgContent(company.logo, company.companyName)
        setLogoSvg(svg)
      }
    }
    fetchLogo()
  }, [company, sourceType])

  if (!advertiserCallout) return null

  const attr =
    advertiserCallout._key && documentId && documentType && parentPath
      ? dataAttr({
          id: documentId,
          type: documentType,
          path: `${parentPath}[_key=="${advertiserCallout._key}"]`,
        })
      : undefined

  return (
    <div
      className={cn(
        'flex flex-col gap-md rounded-lg overflow-hidden w-full max-w-[550px] mx-auto',
        hasTitle ? 'p-2xl' : 'p-6xl',
        themeConfig.container,
        className,
      )}
      {...(attr && { 'data-sanity': attr.toString() })}
    >
      <div className="flex flex-col gap-2xl my-0 mx-auto w-full h-full">
        <div className="flex flex-col gap-0">
          <div
            className={cn(
              'flex flex-col gap-md items-start',
              hasTitle
                ? 'md:flex-row-reverse md:justify-between lg:flex-col xl:flex-row-reverse'
                : 'md:flex-row md:justify-start lg:flex-col xl:flex-row',
            )}
          >
            {company && (
              <div
                className={cn(
                  'flex items-center justify-start',
                  hasTitle
                    ? '[&_svg]:max-w-[120px] [&_img]:max-w-[120px]'
                    : '[&_svg]:max-h-[28px] [&_img]:max-h-[28px]',
                  '[&_svg]:h-auto [&_svg]:w-auto [&_img]:h-auto [&_img]:w-auto',
                  themeConfig.text,
                  !(logoSvg || logoUrl) && 'invisible',
                )}
                style={{
                  minWidth: hasTitle ? '120px' : '28px',
                  minHeight: hasTitle ? '40px' : '28px',
                }}
              >
                {sourceType === 'advertiser' && logoSvg && (
                  <div dangerouslySetInnerHTML={{ __html: logoSvg }} />
                )}
                {sourceType === 'publisherCompany' && logoUrl && (
                  <Image
                    src={logoUrl}
                    alt={company.companyName}
                    width={hasTitle ? 120 : 28}
                    height={hasTitle ? 40 : 28}
                    className={cn('object-contain', getMonochromeFilter())}
                  />
                )}
              </div>
            )}
            {advertiserCallout.title && (
              <h3
                className={cn(
                  'size-header-h2 xl:size-header-h1 2xl:size-display-d2 !mb-0 line-clamp-1',
                  themeConfig.text,
                )}
              >
                {advertiserCallout.title}
              </h3>
            )}
          </div>

          {advertiserCallout.subtitle && (
            <p className={cn('size-header-h6 xl:size-header-h5 !mb-0', themeConfig.text)}>
              {advertiserCallout.subtitle}
            </p>
          )}
        </div>

        {advertiserCallout.text && (
          <p className={cn('size-body-md', themeConfig.text)}>{advertiserCallout.text}</p>
        )}

        {advertiserCallout.showButton && advertiserCallout.button && (
          <div>
            <Button
              button={{
                _type: 'button',
                link: advertiserCallout.button.link,
                icon: advertiserCallout.button.icon ?? null,
                dataId: advertiserCallout.button.dataId ?? null,
                variant: themeConfig.button.variant,
                size: themeConfig.button.size,
                iconPlacement: advertiserCallout.button.icon ? 'left' : 'none',
              }}
              tracking={tracking}
            >
              {advertiserCallout.button.label}
            </Button>
          </div>
        )}
      </div>
    </div>
  )
}
